import React, { useState } from 'react';
import { Alert, Button, Link, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useQuery } from '@apollo/client/react';
import dayjs from 'dayjs';
import { Link as RouterLink } from 'react-router-dom';

import { useRecordContext } from 'components/record/useRecordContext';
import CustomDialog from 'components/forms/CustomDialog';
import { DialogContextProvider } from 'components/dialog/DialogContext';
import { Loader } from 'components/common/General';
import QUERIES from 'services/gqlQueries';
import { FieldProps, getAttributeFromObject } from 'components/list/Fields';

type DeviceEventUsage = {
  event: {
    id: number;
    name: string;
    slug: string;
    startDate?: string;
    endDate?: string;
    status?: string;
  };
  subEvent: {
    id: number;
    name: string;
    sub: string;
  };
};

const DeviceEventsDialogBody = ({ deviceId }: { deviceId: number }) => {
  const { data, loading, error } = useQuery<{ getDeviceEvents: DeviceEventUsage[] }>(QUERIES.getDeviceEvents, {
    variables: { id: deviceId },
    skip: !deviceId,
    fetchPolicy: 'network-only'
  });

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return <Alert severity="error">{error.message}</Alert>;
  }

  const usages = data?.getDeviceEvents ?? [];

  if (!usages.length) {
    return <Typography color="text.secondary">No events found for this device.</Typography>;
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Event</TableCell>
          <TableCell>Sub event</TableCell>
          <TableCell>Start date</TableCell>
          <TableCell>Status</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {usages.map((usage) => (
          <TableRow key={usage.subEvent.id}>
            <TableCell>
              <Link component={RouterLink} to={`/my_events/${usage.event.slug}`}>
                {usage.event.name}
              </Link>
            </TableCell>
            <TableCell>
              <Link component={RouterLink} to={`/sub_events/${usage.subEvent.id}`}>
                {usage.subEvent.name}
                {usage.subEvent.sub ? ` (${usage.subEvent.sub})` : ''}
              </Link>
            </TableCell>
            <TableCell>{usage.event.startDate ? dayjs(usage.event.startDate).format('YYYY-MM-DD') : ''}</TableCell>
            <TableCell>{usage.event.status || ''}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const DeviceEventsField = (props: FieldProps) => {
  const { source } = props;
  const { record: device } = useRecordContext();
  const [open, setOpen] = useState(false);
  const eventCount = Number(getAttributeFromObject(device, source)) ?? 0;

  const handleOpen: React.MouseEventHandler = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (eventCount > 0) {
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <TableCell>
      {eventCount > 0 ? (
        <Link component="button" variant="body2" onClick={handleOpen} sx={{ cursor: 'pointer' }}>
          {eventCount}
        </Link>
      ) : (
        '0'
      )}
      <DialogContextProvider>
        <CustomDialog
          dialogTitle={
            <Stack spacing={0.5}>
              <Typography variant="h6">Device events</Typography>
              <Typography variant="body2" color="text.secondary">
                {device?.name ?? 'Device'}
              </Typography>
            </Stack>
          }
          maxWidth="md"
          primaryButton={
            <Button variant="contained" onClick={handleClose}>
              Close
            </Button>
          }
          secondaryButton={<React.Fragment />}
          open={open}
          resetDialog={handleClose}
        >
          {device?.id ? <DeviceEventsDialogBody deviceId={device.id} /> : null}
        </CustomDialog>
      </DialogContextProvider>
    </TableCell>
  );
};

export default DeviceEventsField;
